import React, { useState, useEffect } from 'react';
import { colors } from '../../Utils/colors';

export interface Notification {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    duration?: number;
    dismissible?: boolean;
}

interface NotificationBarProps {
    notifications: Notification[];
    onDismiss: (id: string) => void;
    position?: 'top' | 'bottom';
    maxNotifications?: number;
}

const NotificationBar: React.FC<NotificationBarProps> = ({
    notifications,
    onDismiss,
    position = 'top',
    maxNotifications = 3
}) => {
    const [visibleNotifications, setVisibleNotifications] = useState<Notification[]>([]);

    useEffect(() => {
        const limitedNotifications = notifications.slice(0, maxNotifications);
        setVisibleNotifications(limitedNotifications);
    }, [notifications, maxNotifications]);

    const getNotificationStyle = (type: Notification['type']) => {
        switch (type) {
            case 'success':
                return {
                    background: colors.background.glass,
                    border: `1px solid ${colors.success.border}`,
                    color: colors.background.glassDark
                };
            case 'error':
                return {
                    background: colors.background.glass,
                    border: `1px solid ${colors.error.border}`,
                    color: colors.background.glassDark
                };
            case 'warning':
                return {
                    background: colors.background.glass,
                    border: `1px solid ${colors.warning.border}`,
                    color: colors.background.glassDark
                };
            case 'info':
                return {
                    background: colors.background.glass,
                    border: `1px solid ${colors.info.border}`,
                    color: colors.background.glassDark
                };
            default:
                return {};
        }
    };

    const getIcon = (type: Notification['type']) => {
        switch (type) {
            case 'success':
                return (
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                    </svg>
                );
            case 'error':
                return (
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"/>
                    </svg>
                );
            case 'warning':
                return (
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                    </svg>
                );
            case 'info':
                return (
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                    </svg>
                );
            default:
                return null;
        }
    };

    if (visibleNotifications.length === 0) {
        return null;
    }

    return (
        <div 
            className={`position-fixed ${position === 'top' ? 'top-0' : 'bottom-0'} start-0 end-0 z-index-1050`}
            style={{ zIndex: 1050 }}
        >
            <div className="container-fluid p-0">
                {visibleNotifications.map((notification) => (
                    <div
                        key={notification.id}
                        className="alert alert-dismissible fade show m-0 border-0 rounded-0"
                        style={getNotificationStyle(notification.type)}
                        role="alert"
                    >
                        <div className="container-fluid">
                            <div className="row align-items-center">
                                <div className="col-auto">
                                    <div className="d-flex align-items-center">
                                        <span className="me-1">
                                            {getIcon(notification.type)}
                                        </span>
                                        <div>
                                            <strong className="me-1">{notification.title}</strong>
                                            <span>{notification.message}</span>
                                        </div>
                                    </div>
                                </div>
                                {notification.dismissible !== false && (
                                    <div className="col-auto ms-auto">
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => onDismiss(notification.id)}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default NotificationBar; 